import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaEye, FaCalendarAlt, FaUser, FaShoppingCart } from 'react-icons/fa';
import { useTitle } from '../../../shared/context/TitleContext';
import { fetchOrders, clearError } from '../../../redux/Slices/AdminOrdersSlice';
import EnhancedPagination from '../../../shared/components/EnhancedPagination';

const AdminOrders = () => {
  const { setTitle } = useTitle();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { orders, pagination, loading, error } = useSelector(state => state.adminOrders);

  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setTitle('sidenav.orders');
    dispatch(clearError());
    fetchOrdersData();
  }, [currentPage]);

  const fetchOrdersData = () => {
    dispatch(fetchOrders({
      page: currentPage
    }));
  };


  const handleViewDetails = (orderId) => {
    navigate(`/admin/orders/${orderId}`);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'placed': { text: 'تم الطلب', class: 'bg-primary' },
      'confirmed': { text: 'مؤكد', class: 'bg-info' },
      'preparing': { text: 'قيد التحضير', class: 'bg-warning' },
      'ready': { text: 'جاهز', class: 'bg-success' },
      'completed': { text: 'مكتمل', class: 'bg-success' },
      'cancelled': { text: 'ملغي', class: 'bg-danger' }
    };
    
    const statusInfo = statusMap[status] || { text: 'غير محدد', class: 'bg-secondary' };
    
    return (
      <span className={`badge ${statusInfo.class} text-white`}>
        {statusInfo.text}
      </span>
    );
  };

  const getPaymentStatusBadge = (status) => {
    const statusMap = {
      'pending': { text: 'في الانتظار', class: 'bg-warning' },
      'paid': { text: 'مدفوع', class: 'bg-success' },
      'failed': { text: 'فشل', class: 'bg-danger' },
      'refunded': { text: 'مسترد', class: 'bg-info' }
    };
    
    const statusInfo = statusMap[status] || { text: 'غير محدد', class: 'bg-secondary' };
    
    return (
      <span className={`badge ${statusInfo.class} text-white`}>
        {statusInfo.text}
      </span>
    );
  };

  const getDeliveryStatusBadge = (status) => {
    const statusMap = {
      'pending': { text: 'في الانتظار', class: 'bg-warning' },
      'assigned': { text: 'تم التخصيص', class: 'bg-info' },
      'picked_up': { text: 'تم الاستلام', class: 'bg-primary' },
      'on_way': { text: 'في الطريق', class: 'bg-warning' },
      'delivered': { text: 'تم التسليم', class: 'bg-success' },
      'failed': { text: 'فشل التسليم', class: 'bg-danger' }
    };
    
    const statusInfo = statusMap[status] || { text: 'غير محدد', class: 'bg-secondary' };
    
    return (
      <span className={`badge ${statusInfo.class} text-white`}>
        {statusInfo.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'غير محدد';
    return new Date(dateString).toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    if (!price || price === 0) return 'غير محدد';
    return `${price} ج.م`;
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const renderPagination = () => {
    const totalPages = pagination?.last_page || pagination?.total_pages || 1;
    const currentPageNumber = pagination?.current_page || currentPage;
    const totalItems = pagination?.total || 0;
    const itemsPerPage = pagination?.per_page || 10;

    if (!pagination) return null;

    return (
      <EnhancedPagination
        currentPage={currentPageNumber}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        showInfo={false}
        className="mt-4"
      />
    );
  };

  return (
    <div className="main_content">
      <div className="dashboard-container">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="d-flex justify-content-between align-items-center mb-4"
        >
          <h1 style={{fontSize: '20px'}} className="fw-bold text-dark mb-0">قائمة الأوردرات</h1>
        </motion.div>

        {/* Orders Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="card shadow-sm"
        >
          <div className="card-body p-3">

            {/* Error Message */}
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            {/* Loading */}
            {loading && (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">جاري التحميل...</span>
                </div>
              </div>
            )}

            {/* Orders Table */}
            {!loading && (
              <div className="table-responsive">
                <table className="table table-modern">
                  <thead>
                    <tr>
                      <th className="text-center">#</th>
                      <th className="text-center">العميل</th>
                      <th className="text-center">رقم التليفون</th>
                      <th className="text-center">المجموع</th>
                      <th className="text-center">حالة الطلب</th>
                      <th className="text-center">حالة الدفع</th>
                      <th className="text-center">حالة التوصيل</th>
                      <th className="text-center">تاريخ الطلب</th>
                      <th className="text-center">الإجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="text-center py-5">
                          <div className="empty-state">
                            <FaShoppingCart className="empty-icon" />
                            <h5 className="mt-3 text-muted">لا توجد أوردرات</h5>
                            <p className="text-muted">لم يتم العثور على أي طلبات في الوقت الحالي</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      orders.map((order, index) => (
                        <tr key={order.id} className="table-row-modern">
                          <td className="text-center">
                            <div className="order-number-badge">
                              #{(currentPage - 1) * 10 + index + 1}
                            </div>
                          </td>
                          <td className="text-center">
                            <div className="customer-info">
                              <span className="customer-name">{order.client || 'غير محدد'}</span>
                            </div>
                          </td>
                          <td className="text-center">
                            <div className="phone-display">
                              {order.client_phone || 'غير محدد'}
                            </div>
                          </td>
                          <td className="text-center">
                            <div className="amount-display">
                              {formatPrice(order.total)}
                            </div>
                          </td>
                          <td className="text-center">{getStatusBadge(order.status)}</td>
                          <td className="text-center">{getPaymentStatusBadge(order.payment_status)}</td>
                          <td className="text-center">{getDeliveryStatusBadge(order.delivery_status)}</td>
                          <td className="text-center">
                            <div className="date-display">
                              {formatDate(order.created_at)}
                            </div>
                          </td>
                          <td className="text-center">
                            <div className="action-buttons">
                            <motion.button
                              whileHover={{ scale: 1.1, y: -2 }}
                              whileTap={{ scale: 0.95 }}
                              className="btn btn-view"
                              onClick={() => handleViewDetails(order.id)}
                              title="عرض التفاصيل"
                            >
                              <FaEye />
                            </motion.button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {!loading && orders.length > 0 && renderPagination()}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminOrders;

// CSS للتصميم الحديث
const style = document.createElement('style');
style.textContent = `
  /* تصميم الجدول الحديث */
  .table-modern {
    background: white;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    border: none;
  }
  
  .table-modern thead {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
  }
  
  .table-modern thead th {
    border: none;
    padding: 1rem;
    font-weight: 600;
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  .table-modern tbody tr {
    border-bottom: 1px solid #f1f5f9;
    transition: all 0.3s ease;
  }
  
  .table-modern tbody tr:hover {
    background-color: #f8fafc;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  .table-modern tbody td {
    border: none;
    padding: 1rem;
    vertical-align: middle;
  }
  
  /* تصميم رقم الطلب */
  .order-number-badge {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-weight: 600;
    font-size: 0.875rem;
    display: inline-block;
    min-width: 60px;
    text-align: center;
  }
  
  /* تصميم معلومات العميل */
  .customer-info {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
  }
  
  .customer-avatar {
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 0.875rem;
  }
  
  /* أيقونات افتراضية للبيانات الفارغة */
  .default-placeholder {
    width: 50px;
    height: 50px;
    background: #f8fafc;
    border: 2px dashed #cbd5e1;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
    font-size: 1.2rem;
    transition: all 0.3s ease;
  }
  
  .default-placeholder:hover {
    background: #f1f5f9;
    border-color: #94a3b8;
    color: #64748b;
  }
  
  .customer-name {
    font-weight: 500;
    color: #374151;
  }
  
  /* تصميم المبلغ */
  .amount-display {
    background: #f0fdf4;
    color: #166534;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-weight: 600;
    font-size: 0.875rem;
    border: 1px solid #bbf7d0;
  }
  
  /* تصميم التاريخ */
  .date-display {
    background: #f8fafc;
    color: #64748b;
    padding: 0.5rem 1rem;
    border-رقم التليفون */
  .phone-display {
    background: #eff6ff;
    color: #1e40af;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-size: 0.875rem;
    border: 1px solid #bfdbfe;
    font-weight: 500;
    direction: ltr;
  }
  
  /* تصميم radius: 8px;
    font-size: 0.875rem;
    border: 1px solid #e2e8f0;
  }
  
  /* تصميم أزرار الإجراءات */
  .action-buttons {
    display: flex;
    gap: 0.5rem;
    justify-content: center;
    flex-direction: row;
    flex-wrap: nowrap;
  }
  
  .btn-view {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    color: white !important;
    padding: 0.25rem;
    border-radius: 8px;
    font-size: 1.2rem;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
    width: 36px;
    height: 36px;
  }
  
  .btn-view * {
    color: white !important;
  }
  
  .btn-view:hover {
    background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }
  
  .btn-text {
    font-size: 0.75rem;
  }
  
  /* تصميم الحالة الفارغة */
  .empty-state {
    padding: 3rem 1rem;
  }
  
  .empty-icon {
    font-size: 4rem;
    color: #cbd5e1;
    margin-bottom: 1rem;
  }
  
  /* تحسين البادج */
  .badge {
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
  
  /* إصلاح الباجينيشن */
  .pagination .page-item.active .page-link {
    background-color: #667eea !important;
    border-color: #667eea !important;
    color: white !important;
  }
  .pagination .page-item .page-link {
    color: #667eea;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    margin: 0 2px;
    padding: 0.5rem 0.75rem;
  }
  .pagination .page-item .page-link:hover {
    color: #5a67d8;
    background-color: #f8fafc;
    border-color: #cbd5e1;
  }
`;
document.head.appendChild(style);
